import axios from 'axios';
import { getAwsAuthHeader } from './AwsSigner';
import configFile from '../aws-exports.json';
import { ensureLeadingSlash, splitQuery, stringifyQuery } from './query';
import { fetchAuthSession } from './Auth';
import { getTenantRegion } from './GetTenantRegion';

const normalizeJsonBody = (data, headers) => {
  const ct = (headers?.['content-type'] || headers?.['Content-Type'] || '').toLowerCase();
  if (data == null) return data;
  if (typeof data === 'string') return data;
  if (ct.includes('application/json')) return JSON.stringify(data);
  return data;
};

const createAxiosClient = () => {
  const client = axios.create({
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  client.interceptors.request.use(
    async (config) => {
      let session;
      try {
        session = await fetchAuthSession();
      } catch (e) {
        console.warn('Fehler beim Abrufen der Auth-Session:', e);
      }

      if (config.bsTokenConfig?.enabled && session?.credentials) {
        try {
          config.headers['bs-token'] = config.bsTokenConfig.prefix + session.idToken.toString();
        } catch (e) {
          console.warn('Fehler beim Abrufen des bs-Tokens:', e);
        }
      }
      if (config.awsSign && session?.credentials) {
        const region = config.awsRegion;
        const baseURL = config.baseURL;
        const method = (config.method || 'GET').toUpperCase();

        if (!region || !baseURL) {
          return config;
        }
        const normalizedBody = normalizeJsonBody(config.data, config.headers);
        if (normalizedBody !== undefined) {
          config.data = normalizedBody;
        }

        // Manchmal übergeben wir auch Query Parameter im Path und nicht als Objekt. Dieser wird hier ausgewertet, falls gesetzt, werden Werte in Params oder queryString übergangen.
        // Außerdem wird sichergestellt das zur Berechnung der Signature Query Parameter in der gleichen Reihenfolge übergeben werden wie im Request
        const basePath = new URL(baseURL).pathname.replace(/\/+$/, '');
        const rawUrl = config.url || '/';
        const { path: pth, search } = splitQuery(rawUrl);
        const urlPathOnly = ensureLeadingSlash(pth || '/');

        const qsFromUrl = search ? `?${search}` : '';
        const qsFromParams = config.params ? stringifyQuery(config.params) : '';
        const qs = qsFromUrl || qsFromParams;

        const signingUrl = `${basePath}${urlPathOnly}${qs}`;

        const authHeader = await getAwsAuthHeader({
          region,
          baseURL,
          params: config.params,
          url: signingUrl,
          method,
          headers: Object.entries(config.headers || {}).reduce((acc, [key, value]) => {
            if (key.toLowerCase() !== 'host') {
              acc[key] = value;
            }
            return acc;
          }, {}),
          data: config.data
        });

        config.headers = Object.entries({
          ...config.headers,
          ...authHeader
        }).reduce((acc, [key, value]) => {
          if (key.toLowerCase() !== 'host') {
            acc[key] = value;
          }
          return acc;
        }, {});
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(error)
  );

  return client;
};

export const apiClient = createAxiosClient();

// Registry for named APIs
const apiRegistry = new Map();
let cachedRegion = null;

/**
 * @typedef {Object} BsTokenConfig
 * @property {boolean} [enabled=true] Enable/disable automatic `bs-token` header enrichment.
 * @property {string}  [prefix='Bearer '] Prefix added before the JWT (e.g., 'Bearer ').
 */

/**
 * @typedef {Object} ApiDefinition
 * @property {string} name Logical API name used for lookups.
 * @property {string} endpoint Base URL of the API (including stage path), e.g. 'https://.../prod'.
 * @property {string} region AWS region used for SigV4 (e.g., 'eu-central-1').
 * @property {boolean} [sign=true] Whether requests to this API are SigV4-signed by default.
 * @property {BsTokenConfig} [bsTokenConfig] Optional per-API bs-token configuration.
 */

/**
 * @typedef {import('axios').AxiosRequestConfig} AxiosRequestConfig
 */

/**
 * Additional options forwarded to axios and token handling.
 *
 * @typedef {AxiosRequestConfig & {
 *   response?: boolean,
 *   bsTokenConfig?: BsTokenConfig
 * }} RequestOptions
 */

/**
 * Configuration object for API requests used by convenience methods.
 *
 * @typedef {Object} RequestConfig
 * @property {Record<string, any>} [params] Query parameters to append.
 * @property {Record<string, any>} [queryStringParameters] Deprecated alias for params (merged).
 * @property {Record<string, string>} [headers] Additional request headers.
 * @property {boolean} [sign] Override per-request SigV4 signing (defaults to API default).
 * @property {RequestOptions} [options] Extra options; forwarded to axios and bs-token handling.
 */

/**
 * Registers APIs from configuration.
 *
 * Clears previous entries and registers all provided API definitions. Caches the
 * associated region to avoid unnecessary re-registration across requests.
 *
 * @param {ApiDefinition[]} apiArray Array of API definitions.
 * @param {string} [region] Region associated with these APIs (cached for reuse).
 * @example
 * // Register APIs for a known region upfront
 * API.register(configFile['eu-central-1'].api, 'eu-central-1');
 */
export const API = {
  register (apiArray, region) {
    if (!Array.isArray(apiArray)) {
      throw new Error('API.register erwartet ein Array von API-Definitionen.');
    }
    apiRegistry.clear();
    apiArray.forEach((api) => {
      if (!api?.name || !api?.endpoint) return;
      const sign = typeof api.sign === 'boolean' ? api.sign : true;

      api.bsTokenConfig = api.bsTokenConfig ? api.bsTokenConfig : {};
      const bsTokenConfig = {
        enabled: true,
        prefix: 'Bearer ',
        ...api.bsTokenConfig
      };

      apiRegistry.set(api.name, {
        name: api.name,
        endpoint: api.endpoint.replace(/\/+$/, ''),
        region: api.region,
        sign,
        bsTokenConfig
      });
    });
    if (region) {
      cachedRegion = region;
    }
  },

  /**
   * Performs a request against a registered API.
   *
   * Auto-registers the current tenant's region on first call or region change.
   * Supports optional SigV4 signing and automatic bs-token header injection.
   *
   * @param {string} apiName Name of the registered API to use.
   * @param {('GET'|'POST'|'PUT'|'PATCH'|'DELETE'|'HEAD'|'OPTIONS'|string)} method HTTP method.
   * @param {string} [path='/'] Request path starting with '/'.
   * @param {Object} [config]
   * @param {Record<string, any>} [config.params] Query parameters to append.
   * @param {Record<string, any>} [config.queryStringParameters] Deprecated alias for params (merged).
   * @param {any} [config.data] Request payload. Auto-serialized if Content-Type is application/json.
   * @param {any} [config.data] Deprecated Alias for Data. Request payload. Auto-serialized if Content-Type is application/json.
   * @param {Record<string, string>} [config.headers] Additional request headers.
   * @param {boolean} [config.sign] Override per-request SigV4 signing (defaults to API default).
   * @param {{
   *   response?: boolean,
   *   bsTokenConfig?: BsTokenConfig,
   *   // Additional axios request options may be provided and are forwarded
   * }} [config.options]
   * @returns {Promise<any>} By default, returns `response.data`. If `options.response === true`, returns full Axios response.
   * @example
   * // Simple GET with query params
   * await API.get('usersApi', '/users', { params: { page: 1, size: 20 } });
   *
   * @example
   * // Signed POST with JSON body
   * await API.post('ordersApi', '/orders', { sku: 'ABC', qty: 2 }, {
   *   headers: { 'Content-Type': 'application/json' },
   *   sign: true
   * });
   *
   * @example
   * // Get full Axios response instead of response.data
   * const res = await API.get('usersApi', '/users/me', { options: { response: true } });
   * console.log(res.status, res.headers);
   *
   * @example
   * // Override bs-token behavior per request (disable)
   * await API.get('secureApi', '/profile', {
   *   options: { bsTokenConfig: { enabled: false } }
   * });
   *
   * @example
   * // Override bs-token prefix per request
   * await API.get('secureApi', '/profile', {
   *   options: { bsTokenConfig: { prefix: 'JWT ' } }
   * });
   */
  async request (apiName, method, path = '/', { params, queryStringParameters, data, body, headers, sign, options = { response: false } } = {}) {
    const regionOfTenant = getTenantRegion();
    if (apiRegistry.size === 0 || cachedRegion !== regionOfTenant) {
      const regionConfig = configFile?.[regionOfTenant];
      if (!regionConfig?.api || !Array.isArray(regionConfig.api)) {
        throw new Error(`API-Konfiguration für Region "${regionOfTenant}" nicht gefunden.`);
      }
      API.register(regionConfig.api, regionOfTenant);
    }
    const api = apiRegistry.get(apiName);
    if (!api) {
      throw new Error(`Unbekannte API: ${apiName}`);
    }
    params = { ...params, ...queryStringParameters };
    data = data || body;
    const urlPathOnly = ensureLeadingSlash(path);
    const qs = stringifyQuery(params);
    const urlWithQs = `${urlPathOnly}${qs}`;

    const shouldSign = typeof sign === 'boolean' ? sign : api.sign;
    const bsTokenConfig = options.bsTokenConfig ? options.bsTokenConfig : api.bsTokenConfig;

    const response = await apiClient.request({
      method,
      baseURL: api.endpoint,
      url: urlWithQs,
      params: undefined,
      data,
      headers,
      awsSign: shouldSign,
      awsRegion: api.region,
      bsTokenConfig,
      ...options
    });
    if (typeof options.response === 'boolean' && options.response) {
      return response;
    }
    return response.data;
  },

  // Convenience methods
  /**
   * Shorthand for a GET request.
   * @param {string} apiName Name of the registered API to use.
   * @param {string} [path='/'] Request path starting with '/'.
   * @param {{headers: {} & *}} [config] Optional request configuration.
   * @returns {Promise<any>} Resolves to `response.data` by default, or full Axios response if `options.response === true`.
   */
  get (apiName, path = '/', config = {}) {
    return this.request(apiName, 'GET', path, config);
  },
  /**
   * Shorthand for a DELETE request.
   * @param {string} apiName Name of the registered API to use.
   * @param {string} [path='/'] Request path starting with '/'.
   * @param {RequestConfig} [config] Optional request configuration.
   * @returns {Promise<any>} Resolves to `response.data` by default, or full Axios response if `options.response === true`.
   */
  del (apiName, path = '/', config = {}) {
    return this.request(apiName, 'DELETE', path, config);
  },
  /**
   * Shorthand for a HEAD request.
   * @param {string} apiName Name of the registered API to use.
   * @param {string} [path='/'] Request path starting with '/'.
   * @param {RequestConfig} [config] Optional request configuration.
   * @returns {Promise<any>} Resolves to `response.data` by default, or full Axios response if `options.response === true`.
   */
  head (apiName, path = '/', config = {}) {
    return this.request(apiName, 'HEAD', path, config);
  },
  /**
   * Shorthand for an OPTIONS request.
   * @param {string} apiName Name of the registered API to use.
   * @param {string} [path='/'] Request path starting with '/'.
   * @param {RequestConfig} [config] Optional request configuration.
   * @returns {Promise<any>} Resolves to `response.data` by default, or full Axios response if `options.response === true`.
   */
  options (apiName, path = '/', config = {}) {
    return this.request(apiName, 'OPTIONS', path, config);
  },
  /**
   * Shorthand for a POST request.
   * @param {string} apiName Name of the registered API to use.
   * @param {string} [path='/'] Request path starting with '/'.
   * @param {RequestConfig} [config] Optional request configuration.
   * @returns {Promise<any>} Resolves to `response.data` by default, or full Axios response if `options.response === true`.
   */
  post (apiName, path = '/', config = {}) {
    return this.request(apiName, 'POST', path, config);
  },
  /**
   * Shorthand for a PUT request.
   * @param {string} apiName Name of the registered API to use.
   * @param {string} [path='/'] Request path starting with '/'.
   * @param {RequestConfig} [config] Optional request configuration.
   * @returns {Promise<any>} Resolves to `response.data` by default, or full Axios response if `options.response === true`.
   */
  put (apiName, path = '/', config = {}) {
    return this.request(apiName, 'PUT', path, config);
  },
  /**
   * Shorthand for a PATCH request.
   * @param {string} apiName Name of the registered API to use.
   * @param {string} [path='/'] Request path starting with '/'.
   * @param {RequestConfig} [config] Optional request configuration.
   * @returns {Promise<any>} Resolves to `response.data` by default, or full Axios response if `options.response === true`.
   */
  patch (apiName, path = '/', config = {}) {
    return this.request(apiName, 'PATCH', path, config);
  }
};
